/*
 * The signed-in user's notifications: an initial page from the API, then
 * `notification.created` / `notification.updated` events from the shared
 * realtime stream (polled every 30s while that stream is down). Arrivals
 * while the tab is visible raise a toast unless the user is already looking
 * at the linked page. Mounted once in the authenticated shell.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useRealtime, useRealtimeEvent } from "@/lib/realtime/RealtimeProvider";

import { NotificationsAPI } from "./api/notificationsApi";
import { renderNotification, rendererFor } from "./registry";
import {
  isUnread,
  type Notification,
  type NotificationPreferences,
  type TypePreference
} from "./types";

export const PAGE_SIZE = 50;
export const FALLBACK_POLL_MS = 30_000;

type State = {
  items: Notification[];
  unreadCount: number;
  hasUnseen: boolean;
  nextCursor: number | null;
  loading: boolean;
  error: string | null;
};

export type NotificationsContextValue = State & {
  /** Clear the "new" dot (popover opened). */
  markSeen: () => Promise<void>;
  markRead: (ids: string[]) => Promise<void>;
  /** `context` such as `{thread_id}` limits the sweep. */
  markAllRead: (context?: Record<string, unknown> | null) => Promise<void>;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  /** Unread rows whose `context` matches every given key. */
  unreadFor: (context: Record<string, unknown>) => Notification[];
  preferences: NotificationPreferences | null;
  loadPreferences: () => Promise<NotificationPreferences>;
  updatePreferences: (patch: {
    quiet?: boolean;
    per_type?: Record<string, Partial<TypePreference>>;
  }) => Promise<NotificationPreferences>;
};

const NotificationsContext = createContext<NotificationsContextValue | null>(
  null
);

/** Pause All or the type's In-App switch off means no toast. */
export const toastAllowed = (
  preferences: NotificationPreferences | null,
  type: string
): boolean =>
  !preferences ||
  (!preferences.quiet && preferences.per_type[type]?.in_app !== false);

const byNewest = (a: Notification, b: Notification) =>
  (b.created_at_ms ?? 0) - (a.created_at_ms ?? 0);

/** Insert or replace by id, newest first. */
export const upsert = (
  items: Notification[],
  next: Notification
): Notification[] => {
  const rest = items.filter((n) => n.id !== next.id);
  return [next, ...rest].sort(byNewest);
};

// The chat threads currently on screen, published by whichever views show one
// (the Case IDE pane, the agent panel) keyed by owner, so a mention in an open
// thread is not toasted (avoids a chat→notifications import).
const openThreads = new Map<string, string>();
export const CASE_PANE_OWNER = "case-ide";
export const setViewedThread = (owner: string, id: string | null) => {
  if (id) openThreads.set(owner, id);
  else openThreads.delete(owner);
};
/** The Case IDE pane's thread (legacy single-owner API). */
export const setCurrentThreadId = (id: string | null) =>
  setViewedThread(CASE_PANE_OWNER, id);
export const currentThreadId = () => openThreads.get(CASE_PANE_OWNER) ?? null;
export const isThreadViewed = (id: string): boolean => {
  for (const open of openThreads.values()) if (open === id) return true;
  return false;
};
/** Open in a view that travels across pages (the agent panel), not the case pane. */
const isThreadViewedGlobally = (id: string): boolean => {
  for (const [owner, open] of openThreads)
    if (open === id && owner !== CASE_PANE_OWNER) return true;
  return false;
};

// `/cases/<id>/...` or `/clients/<fn>/cases/<id>/...` → the case id.
const caseIdOf = (path: string): string | null =>
  path.match(/\/cases\/([^/?#]+)/)?.[1] ?? null;

/** Is the user already looking at what this notification links to? */
export const isViewing = (
  link: string | null,
  pathname: string,
  search: string
) => {
  if (!link) return false;
  const [path, query = ""] = link.split("?");
  const thread = new URLSearchParams(query).get("thread");
  // A thread open in the global agent panel travels across pages, so it counts
  // as being looked at wherever the user is.
  if (thread && isThreadViewedGlobally(thread)) return true;
  const linkCase = caseIdOf(path);
  const samePage = linkCase
    ? linkCase === caseIdOf(pathname)
    : path === pathname;
  if (!samePage) return false;
  if (!thread) return true;
  // Same page: only "already on that thread" counts (the URL or the case pane).
  const have = new URLSearchParams(search).get("thread");
  return have === thread || currentThreadId() === thread;
};

type Props = { children: ReactNode; enabled?: boolean };

export const NotificationsProvider: FC<Props> = ({
  children,
  enabled = true
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;
  const { status } = useRealtime();

  const [state, setState] = useState<State>({
    items: [],
    unreadCount: 0,
    hasUnseen: false,
    nextCursor: null,
    loading: true,
    error: null
  });
  const [preferences, setPreferences] =
    useState<NotificationPreferences | null>(null);
  const preferencesRef = useRef(preferences);
  preferencesRef.current = preferences;
  const loaded = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const page = await NotificationsAPI.list({ limit: PAGE_SIZE });
      setState((cur) => ({
        ...cur,
        items: page.items.slice().sort(byNewest),
        unreadCount: page.unread_count,
        hasUnseen: page.has_unseen,
        nextCursor: page.next_cursor,
        loading: false,
        error: null
      }));
      loaded.current = true;
    } catch (e) {
      setState((cur) => ({
        ...cur,
        loading: false,
        error: (e as Error).message ?? "Couldn't load notifications"
      }));
    }
  }, []);

  useEffect(() => {
    if (enabled) void refresh();
  }, [enabled, refresh]);
  useEffect(() => {
    if (!enabled) return;
    NotificationsAPI.getPreferences()
      .then(setPreferences)
      .catch(() => {});
  }, [enabled]);

  // Polling fallback while the stream is down (not while paused: hidden tab).
  useEffect(() => {
    if (!enabled) return undefined;
    if (status !== "closed" && status !== "connecting") return undefined;
    const timer = setInterval(() => void refresh(), FALLBACK_POLL_MS);
    return () => clearInterval(timer);
  }, [enabled, status, refresh]);
  // Reconnected after a gap: notifications have no replay cursor, so refetch.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (status === "open") {
      if (wasOpen.current) void refresh();
      wasOpen.current = true;
    }
  }, [status, refresh]);

  const apply = useCallback(
    (n: Notification, created: boolean) => {
      setState((cur) => {
        const existed = cur.items.some((x) => x.id === n.id);
        const prev = cur.items.find((x) => x.id === n.id);
        const items = upsert(cur.items, n);
        // Recompute the counters locally; the server is the source of truth on
        // the next list call.
        let { unreadCount } = cur;
        if (!existed && isUnread(n)) unreadCount += 1;
        else if (prev && isUnread(prev) && !isUnread(n))
          unreadCount = Math.max(0, unreadCount - 1);
        else if (prev && !isUnread(prev) && isUnread(n)) unreadCount += 1;
        const hasUnseen = cur.hasUnseen || (isUnread(n) && !n.seen_at);
        return { ...cur, items, unreadCount, hasUnseen };
      });
      if (!created || rendererFor(n.type).quiet) return;
      if (!toastAllowed(preferencesRef.current, n.type)) return;
      if (
        typeof document !== "undefined" &&
        document.visibilityState !== "visible"
      )
        return;
      const { pathname, search } = locationRef.current;
      if (isViewing(n.link, pathname, search)) return;
      const rendered = renderNotification(n);
      toast(rendered.title, {
        id: `notification-${n.id}`,
        description: rendered.description ?? undefined,
        action: n.link
          ? {
              label: rendered.action ?? "Open",
              onClick: () => {
                void NotificationsAPI.markRead([n.id]).catch(() => {});
                setState((cur) => ({
                  ...cur,
                  items: cur.items.map((x) =>
                    x.id === n.id && !x.read_at
                      ? { ...x, read_at: new Date().toISOString() }
                      : x
                  ),
                  unreadCount: isUnread(n)
                    ? Math.max(0, cur.unreadCount - 1)
                    : cur.unreadCount
                }));
                navigate(n.link as string);
              }
            }
          : undefined
      });
    },
    [navigate]
  );

  useRealtimeEvent<Notification>("notification.created", (n) => {
    if (n?.id) apply(n, true);
  });
  useRealtimeEvent<Notification>("notification.updated", (n) => {
    // A grouped repeat arrives as an update of the existing unread row; toast
    // it like a new one when its count grew.
    if (!n?.id) return;
    const prev = state.items.find((x) => x.id === n.id);
    apply(n, !!prev && isUnread(n) && (n.count ?? 1) > (prev.count ?? 1));
  });

  const markSeen = useCallback(async () => {
    setState((cur) => (cur.hasUnseen ? { ...cur, hasUnseen: false } : cur));
    await NotificationsAPI.markSeen().catch(() => {});
  }, []);

  const markRead = useCallback(async (ids: string[]) => {
    if (!ids.length) return;
    const now = new Date().toISOString();
    setState((cur) => {
      const set = new Set(ids);
      let dec = 0;
      const items = cur.items.map((n) => {
        if (!set.has(n.id) || n.read_at) return n;
        dec += 1;
        return { ...n, read_at: now };
      });
      return { ...cur, items, unreadCount: Math.max(0, cur.unreadCount - dec) };
    });
    try {
      const { unread_count } = await NotificationsAPI.markRead(ids);
      setState((cur) => ({ ...cur, unreadCount: unread_count }));
    } catch {
      // Optimistic state stands; the next refresh reconciles.
    }
  }, []);

  const markAllRead = useCallback(
    async (context?: Record<string, unknown> | null) => {
      const now = new Date().toISOString();
      const matches = (n: Notification) =>
        !context ||
        Object.entries(context).every(([k, v]) => n.context?.[k] === v);
      setState((cur) => {
        let dec = 0;
        const items = cur.items.map((n) => {
          if (n.read_at || !matches(n)) return n;
          dec += 1;
          return { ...n, read_at: now };
        });
        if (!dec && cur.unreadCount === 0) return cur;
        return {
          ...cur,
          items,
          unreadCount: context ? Math.max(0, cur.unreadCount - dec) : 0
        };
      });
      try {
        const { unread_count } = await NotificationsAPI.markAllRead(context);
        setState((cur) => ({ ...cur, unreadCount: unread_count }));
      } catch {
        // See markRead.
      }
    },
    []
  );

  const loadMore = useCallback(async () => {
    const cursor = state.nextCursor;
    if (cursor == null) return;
    const page = await NotificationsAPI.list({ cursor, limit: PAGE_SIZE });
    setState((cur) => ({
      ...cur,
      items: page.items.reduce(upsert, cur.items),
      nextCursor: page.next_cursor,
      unreadCount: page.unread_count
    }));
  }, [state.nextCursor]);

  const unreadFor = useCallback(
    (context: Record<string, unknown>) =>
      state.items.filter(
        (n) =>
          isUnread(n) &&
          Object.entries(context).every(([k, v]) => n.context?.[k] === v)
      ),
    [state.items]
  );

  const loadPreferences = useCallback(async () => {
    const p = await NotificationsAPI.getPreferences();
    setPreferences(p);
    return p;
  }, []);
  const updatePreferences = useCallback(
    async (patch: {
      quiet?: boolean;
      per_type?: Record<string, Partial<TypePreference>>;
    }) => {
      const p = await NotificationsAPI.updatePreferences(patch);
      setPreferences(p);
      return p;
    },
    []
  );

  const value = useMemo<NotificationsContextValue>(
    () => ({
      ...state,
      markSeen,
      markRead,
      markAllRead,
      loadMore,
      refresh,
      unreadFor,
      preferences,
      loadPreferences,
      updatePreferences
    }),
    [
      state,
      markSeen,
      markRead,
      markAllRead,
      loadMore,
      refresh,
      unreadFor,
      preferences,
      loadPreferences,
      updatePreferences
    ]
  );
  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

const noopAsync = async () => {};
const INERT: NotificationsContextValue = {
  items: [],
  unreadCount: 0,
  hasUnseen: false,
  nextCursor: null,
  loading: false,
  error: null,
  markSeen: noopAsync,
  markRead: noopAsync,
  markAllRead: noopAsync,
  loadMore: noopAsync,
  refresh: noopAsync,
  unreadFor: () => [],
  preferences: null,
  loadPreferences: async () => ({ quiet: false, per_type: {}, types: [] }),
  updatePreferences: async () => ({ quiet: false, per_type: {}, types: [] })
};

/** Inert outside the provider so consumers (the case chat) need no guard. */
export const useNotifications = (): NotificationsContextValue =>
  useContext(NotificationsContext) ?? INERT;
